import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function HomePage() {
  // Schema.org JSON-LD
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const personSchema = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Adrian M. Hurtado',
      url: siteUrl,
      image: 'https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/e9b4463a34ddb160942b6a3ff8748621.jpeg',
      jobTitle: 'Author',
      description: 'Adrian M. Hurtado is a children\'s book author, former teacher and principal with over 25 years of experience in education across Orange County, Los Angeles County, and Santa Barbara.',
      sameAs: ['https://www.amazon.com/stores/Adrian-M.-Hurtado/author/B00JER2ZOE'],
    };

    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Adrian M. Hurtado',
      url: siteUrl,
      description: 'Official website of children\'s book author Adrian M. Hurtado. Explore books, audiobooks, and more.',
      author: { '@type': 'Person', name: 'Adrian M. Hurtado' },
    };

    const bookListSchema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Books by Adrian M. Hurtado',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'The Dawn', url: 'https://www.amazon.com/Dawn-Adrian-M-Hurtado-ebook/dp/B0FW6TVDH4' },
        { '@type': 'ListItem', position: 2, name: 'Inside Me', url: 'https://www.amazon.com/Inside-Me-Adrian-M-Hurtado-ebook/dp/B0FVHKHXPX' },
        { '@type': 'ListItem', position: 3, name: 'Mary Had A Little Lamb', url: 'https://www.amazon.com/Mary-Little-Lamb-Adrian-Hurtado-ebook/dp/B0BGBW9K8T' },
        { '@type': 'ListItem', position: 4, name: 'The Tennis Jock', url: 'https://www.amazon.com/Tennis-Jock-Adrian-M-Hurtado-ebook/dp/B0FWG8TQV8' },
        { '@type': 'ListItem', position: 5, name: 'Acro Bat', url: 'https://www.amazon.com/Acro-Bat-Adrian-M-Hurtado-ebook/dp/B0FLSLS37X' },
        { '@type': 'ListItem', position: 6, name: "Toad's Tools", url: 'https://www.amazon.com/Toads-Tools-Adrian-M-Hurtado-ebook/dp/B0FLS91T7Z' },
        { '@type': 'ListItem', position: 7, name: 'The Patrol', url: 'https://www.amazon.com/PATROL-Adrian-M-Hurtado-ebook/dp/B0FVHC1M7S' },
        { '@type': 'ListItem', position: 8, name: 'El murciélago Acro Bat', url: 'https://www.amazon.com/El-murci%C3%A9lago-Acro-Bat-Spanish-ebook/dp/B0FSP6ZDTS' },
        { '@type': 'ListItem', position: 9, name: 'Herramientas del sapo Toad', url: 'https://www.amazon.com/Herramientas-del-sapo-Toad-Spanish-ebook/dp/B0FSNRQN72' },
      ],
    };

    const scripts: HTMLScriptElement[] = [];

    [personSchema, websiteSchema, bookListSchema].forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      script.setAttribute('data-schema', 'home');
      document.head.appendChild(script);
      scripts.push(script);
    });

    document.title = 'Adrian M. Hurtado | Children\'s Books Author & Educator';

    return () => {
      scripts.forEach((s) => s.parentNode?.removeChild(s));
    };
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d504q4lejf078mfssj50', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  type Book = {
    title: string;
    image: string;
    price: string;
    link: string;
    rating?: string;
    label?: string;
  };

  const books: Book[] = [
    {
      title: 'The Dawn',
      image: 'https://m.media-amazon.com/images/I/81Cw42zfDML._SY522_.jpg',
      price: '$3.99',
      link: 'https://www.amazon.com/Dawn-Adrian-M-Hurtado-ebook/dp/B0FW6TVDH4'
    },
    {
      title: 'Inside Me',
      image: 'https://m.media-amazon.com/images/I/81bxLzKr-KL._SY522_.jpg',
      price: '$3.99',
      link: 'https://www.amazon.com/Inside-Me-Adrian-M-Hurtado-ebook/dp/B0FVHKHXPX'
    },
    {
      title: 'Mary Had A Little Lamb',
      image: 'https://m.media-amazon.com/images/I/810E-rIOBGL._SY522_.jpg',
      price: '$3.99',
      rating: '5.0',
      link: 'https://www.amazon.com/Mary-Little-Lamb-Adrian-Hurtado-ebook/dp/B0BGBW9K8T'
    },
    {
      title: 'The Tennis Jock',
      image: 'https://m.media-amazon.com/images/I/71IPF8843NL._SY522_.jpg',
      price: '$3.99',
      link: 'https://www.amazon.com/Tennis-Jock-Adrian-M-Hurtado-ebook/dp/B0FWG8TQV8'
    },
    {
      title: 'Acro Bat',
      image: 'https://m.media-amazon.com/images/I/91AtHwzLmGL._SY522_.jpg',
      price: '$3.99',
      rating: '5.0',
      link: 'https://www.amazon.com/Acro-Bat-Adrian-M-Hurtado-ebook/dp/B0FLSLS37X'
    },
    {
      title: "Toad's Tools",
      image: 'https://m.media-amazon.com/images/I/81h0TU89dPL._SY522_.jpg',
      price: '$3.99',
      rating: '5.0',
      link: 'https://www.amazon.com/Toads-Tools-Adrian-M-Hurtado-ebook/dp/B0FLS91T7Z'
    },
    {
      title: 'The Patrol',
      image: 'https://m.media-amazon.com/images/P/B0FVHC1M7S.01._SX522_SCLZZZZZZZ_.jpg',
      price: '$3.99',
      link: 'https://www.amazon.com/PATROL-Adrian-M-Hurtado-ebook/dp/B0FVHC1M7S'
    },
    {
      title: 'El murciélago Acro Bat',
      image: 'https://m.media-amazon.com/images/P/B0FSP6ZDTS.01._SX522_SCLZZZZZZZ_.jpg',
      price: '$3.99',
      label: 'Spanish Edition',
      link: 'https://www.amazon.com/El-murci%C3%A9lago-Acro-Bat-Spanish-ebook/dp/B0FSP6ZDTS'
    },
    {
      title: 'Herramientas del sapo Toad',
      image: 'https://m.media-amazon.com/images/P/B0FSNRQN72.01._SX522_SCLZZZZZZZ_.jpg',
      price: '$3.99',
      label: 'Spanish Edition',
      link: 'https://www.amazon.com/Herramientas-del-sapo-Toad-Spanish-ebook/dp/B0FSNRQN72'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-amber-50 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        {/* Gradient Orbs - More masculine colors */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-orange-300/30 via-amber-300/25 to-transparent rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-slate-400/30 via-orange-300/25 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-0 left-1/4 w-[700px] h-[700px] bg-gradient-to-tr from-amber-400/25 via-slate-400/20 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        
        {/* Floating Shapes - Stronger, bolder shapes */}
        <div className="absolute top-20 left-[10%] w-28 h-28 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg rotate-12 animate-bounce-slow opacity-50"></div>
        <div className="absolute top-40 right-[15%] w-24 h-24 bg-gradient-to-br from-slate-600 to-slate-700 rounded-lg -rotate-12 animate-bounce-slow opacity-50" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute bottom-40 left-[20%] w-32 h-32 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg rotate-45 animate-bounce-slow opacity-50" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-60 right-[25%] w-20 h-20 bg-gradient-to-br from-slate-500 to-orange-500 rounded-lg -rotate-45 animate-bounce-slow opacity-50" style={{ animationDelay: '1.5s' }}></div>
        
        {/* Stars - More subtle */}
        <div className="absolute top-[15%] left-[25%] w-8 h-8 animate-spin-slow opacity-60">
          <i className="ri-star-fill text-amber-500 text-3xl"></i>
        </div>
        <div className="absolute top-[45%] right-[30%] w-8 h-8 animate-spin-slow opacity-60" style={{ animationDelay: '1s' }}>
          <i className="ri-star-fill text-orange-500 text-2xl"></i>
        </div>
        <div className="absolute bottom-[35%] left-[35%] w-8 h-8 animate-spin-slow opacity-60" style={{ animationDelay: '2s' }}>
          <i className="ri-star-fill text-slate-500 text-3xl"></i>
        </div>
        <div className="absolute top-[70%] right-[15%] w-8 h-8 animate-spin-slow opacity-60" style={{ animationDelay: '0.5s' }}>
          <i className="ri-star-fill text-amber-500 text-2xl"></i>
        </div>
        
        {/* Geometric elements */}
        <div className="absolute top-[25%] left-[45%] w-6 h-6 bg-orange-500 rotate-45 animate-float opacity-60"></div>
        <div className="absolute top-[55%] right-[40%] w-6 h-6 bg-slate-600 rotate-12 animate-float opacity-60" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-[45%] left-[15%] w-6 h-6 bg-amber-500 rotate-45 animate-float opacity-60" style={{ animationDelay: '1.5s' }}></div>
        <div className="absolute top-[80%] right-[50%] w-6 h-6 bg-orange-500 rotate-12 animate-float opacity-60" style={{ animationDelay: '0.5s' }}></div>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-md z-50 border-b-4 border-gradient-to-r from-slate-700 via-orange-500 to-amber-500">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/e9b4463a34ddb160942b6a3ff8748621.jpeg" 
                alt="Adrian Hurtado Logo" 
                className="w-12 h-12 rounded-full object-cover ring-4 ring-orange-500 shadow-lg"
              />
              <span className="text-xl font-bold bg-gradient-to-r from-slate-700 via-orange-600 to-amber-600 bg-clip-text text-transparent">Adrian M. Hurtado</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#home" className="text-sm font-bold text-orange-600 hover:text-amber-600 transition-colors whitespace-nowrap cursor-pointer">Home</a>
              <a href="#about" className="text-sm font-bold text-gray-700 hover:text-orange-600 transition-colors whitespace-nowrap cursor-pointer">About the Author</a>
              <a href="#books" className="text-sm font-bold text-gray-700 hover:text-orange-600 transition-colors whitespace-nowrap cursor-pointer">Books</a>
              <Link to="/audiobooks" className="text-sm font-bold text-gray-700 hover:text-orange-600 transition-colors whitespace-nowrap cursor-pointer">Audiobooks</Link>
              <a href="#contact" className="text-sm font-bold text-gray-700 hover:text-orange-600 transition-colors whitespace-nowrap cursor-pointer">Contact Us</a>
            </div>
            <button className="md:hidden text-orange-600">
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Home Section */}
      <section id="home" className="pt-32 pb-20 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-slate-200 via-orange-200 to-amber-200 rounded-full mb-6 shadow-lg animate-bounce-gentle">
                <i className="ri-book-open-line text-orange-600 text-xl"></i>
                <span className="text-sm font-bold text-gray-800">Children's Books</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-black bg-gradient-to-r from-slate-700 via-orange-600 to-amber-600 bg-clip-text text-transparent mb-6 leading-tight">
                Welcome to the World of Adrian M. Hurtado
              </h1>
              <p className="text-lg text-gray-800 mb-8 leading-relaxed font-medium">
                Discover captivating stories that inspire imagination and touch hearts. From children's tales to thought-provoking narratives, explore a collection of books crafted with passion and creativity.
              </p>
              <div className="flex gap-4">
                <a 
                  href="#books" 
                  className="px-8 py-4 bg-gradient-to-r from-slate-700 via-orange-600 to-amber-600 text-white text-sm font-black rounded-lg hover:scale-105 transition-all shadow-xl shadow-orange-300 whitespace-nowrap cursor-pointer"
                >
                  📚 Explore Books
                </a>
                <a 
                  href="#about" 
                  className="px-8 py-4 border-3 border-slate-700 text-slate-700 text-sm font-black rounded-lg hover:bg-slate-50 hover:scale-105 transition-all whitespace-nowrap cursor-pointer shadow-lg"
                >
                  Learn More
                </a>
              </div>
            </div>
            <div className="relative z-10">
              <div className="aspect-square rounded-full overflow-hidden shadow-2xl shadow-orange-300 ring-8 ring-gradient-to-r from-slate-600 via-orange-500 to-amber-500 relative animate-float">
                <img 
                  src="https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/e9b4463a34ddb160942b6a3ff8748621.jpeg" 
                  alt="Adrian M. Hurtado" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full blur-3xl opacity-60 animate-pulse"></div>
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-br from-slate-600 to-orange-500 rounded-full blur-2xl opacity-50 animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* About the Author Section */}
      <section id="about" className="py-20 px-6 relative">
        <div className="max-w-5xl mx-auto relative z-10">
          <h2 className="text-4xl font-black bg-gradient-to-r from-slate-700 via-orange-600 to-amber-600 bg-clip-text text-transparent mb-12 text-center">About the Author</h2>
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 md:p-12 shadow-2xl shadow-orange-200 border-3 border-orange-300">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-800 leading-relaxed mb-6 font-medium">
                Adrian M. Hurtado was born in Berkeley, CA and raised in the East Bay area. He is a former teacher and principal, having served more than 25 years in schools in Orange County, Los Angeles County, and Santa Barbara.
              </p>
              <p className="text-gray-800 leading-relaxed mb-6 font-medium">
                With a deep passion for education and storytelling, Adrian has dedicated his career to inspiring young minds and fostering creativity. His experiences in the classroom and as an educational leader have profoundly influenced his writing, bringing authenticity and heart to every story he tells.
              </p>
              <p className="text-gray-800 leading-relaxed font-medium">
                Through his books, Adrian continues his mission to educate, entertain, and empower readers of all ages. His works reflect his commitment to creating meaningful narratives that resonate with children and adults alike.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Books Section */}
      <section id="books" className="py-20 px-6 relative">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-slate-200 via-orange-200 to-amber-200 rounded-full mb-4 shadow-lg">
              <i className="ri-book-2-line text-orange-600 text-xl"></i>
              <span className="text-sm font-bold text-gray-800">Our Collection</span>
            </div>
            <h2 className="text-4xl font-black bg-gradient-to-r from-slate-700 via-orange-600 to-amber-600 bg-clip-text text-transparent mb-4">Books</h2>
            <p className="text-gray-800 font-medium">Explore the complete collection of published works</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {books.map((book, index) => (
              <div key={index} className="group cursor-pointer transform hover:scale-105 transition-all duration-300">
                <a href={book.link} target="_blank" rel="noopener noreferrer" className="block">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl overflow-hidden shadow-xl shadow-orange-200 hover:shadow-2xl hover:shadow-amber-300 transition-all duration-300 border-3 border-orange-300 hover:border-amber-400">
                    <div className="w-full h-80 overflow-hidden bg-gradient-to-br from-slate-100 via-orange-100 to-amber-100 flex items-center justify-center">
                      <img 
                        src={book.image} 
                        alt={book.title}
                        className="h-full w-auto object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6 bg-gradient-to-br from-white to-orange-50">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="text-lg font-black text-gray-900">{book.title}</h3>
                        {book.label && (
                          <span className="shrink-0 px-2 py-1 bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700 text-xs font-bold rounded-md whitespace-nowrap">
                            {book.label}
                          </span>
                        )}
                      </div>
                      {book.rating && (
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex gap-1">
                            {[...Array(5)].map((_, i) => (
                              <i key={i} className="ri-star-fill text-amber-500 text-sm"></i>
                            ))}
                          </div>
                          <span className="text-sm font-bold text-gray-600">{book.rating}</span>
                        </div>
                      )}
                      <p className="text-xl font-black bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">{book.price}</p>
                      <p className="text-sm text-gray-600 mt-2 font-medium">Kindle Edition</p>
                    </div>
                  </div>
                </a>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a 
              href="https://www.amazon.com/stores/Adrian-M.-Hurtado/author/B00JER2ZOE" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-slate-700 via-orange-600 to-amber-600 text-white text-sm font-black rounded-lg hover:scale-105 transition-all shadow-xl shadow-orange-300 whitespace-nowrap cursor-pointer"
            >
              View All Books on Amazon
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section id="contact" className="py-20 px-6 relative">
        <div className="max-w-3xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-slate-200 via-orange-200 to-amber-200 rounded-full mb-4 shadow-lg">
              <i className="ri-mail-line text-orange-600 text-xl"></i>
              <span className="text-sm font-bold text-gray-800">Get in Touch</span>
            </div>
            <h2 className="text-4xl font-black bg-gradient-to-r from-slate-700 via-orange-600 to-amber-600 bg-clip-text text-transparent mb-4">Contact Us</h2>
            <p className="text-gray-800 font-medium">Have a question or want to get in touch? Send us a message.</p>
          </div>
          
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 md:p-12 shadow-2xl shadow-orange-200 border-3 border-orange-300">
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-gray-900 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-orange-300 rounded-lg focus:ring-4 focus:ring-orange-300 focus:border-orange-500 outline-none transition-all text-sm font-medium"
                    placeholder="Your name"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-gray-900 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-orange-300 rounded-lg focus:ring-4 focus:ring-orange-300 focus:border-orange-500 outline-none transition-all text-sm font-medium"
                    placeholder="your.email@example.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-bold text-gray-900 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 border-2 border-orange-300 rounded-lg focus:ring-4 focus:ring-orange-300 focus:border-orange-500 outline-none transition-all resize-none text-sm font-medium"
                    placeholder="Your message (max 500 characters)"
                  />
                  <p className="text-xs text-gray-500 mt-1 font-medium">{formData.message.length}/500 characters</p>
                </div>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-100 border-2 border-green-300 rounded-lg">
                    <p className="text-sm text-green-800 font-bold">Thank you for your message! We'll get back to you soon.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-100 border-2 border-red-300 rounded-lg">
                    <p className="text-sm text-red-800 font-bold">Something went wrong. Please try again later.</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || formData.message.length > 500}
                  className="w-full px-8 py-4 bg-gradient-to-r from-slate-700 via-orange-600 to-amber-600 text-white text-sm font-black rounded-lg hover:scale-105 transition-all shadow-xl shadow-orange-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Sending...' : '✉️ Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-slate-800 via-orange-600 to-amber-600 text-white py-12 px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://static.readdy.ai/image/09a869d581523d35fa4e4396fbda86f5/e9b4463a34ddb160942b6a3ff8748621.jpeg" 
                  alt="Adrian Hurtado Logo" 
                  className="w-10 h-10 rounded-full object-cover ring-4 ring-white shadow-lg"
                />
                <span className="text-lg font-black">Adrian M. Hurtado</span>
              </div>
              <p className="text-sm font-bold text-white/90">
                Author, Educator, Storyteller
              </p>
            </div>
            
            <div>
              <h3 className="text-sm font-black mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><a href="#home" className="text-sm font-bold text-white/90 hover:text-white transition-colors cursor-pointer">Home</a></li>
                <li><a href="#about" className="text-sm font-bold text-white/90 hover:text-white transition-colors cursor-pointer">About</a></li>
                <li><a href="#books" className="text-sm font-bold text-white/90 hover:text-white transition-colors cursor-pointer">Books</a></li>
                <li><Link to="/audiobooks" className="text-sm font-bold text-white/90 hover:text-white transition-colors cursor-pointer">Audiobooks</Link></li>
                <li><a href="#contact" className="text-sm font-bold text-white/90 hover:text-white transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-sm font-black mb-4">Connect</h3>
              <div className="flex gap-4">
                <a href="https://www.amazon.com/stores/Adrian-M.-Hurtado/author/B00JER2ZOE" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/30 rounded-lg flex items-center justify-center hover:bg-white/50 transition-colors cursor-pointer shadow-lg">
                  <i className="ri-amazon-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t-2 border-white/30 pt-8 text-center">
            <p className="text-sm font-bold text-white/90">
              © 2025 Adrian M. Hurtado. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">Powered by Leavitt Peak Press</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
